import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { FormsModule } from '@angular/forms';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import {
  RappiderButtonModule,
  RappiderCardWithIconTitleDescriptionListComponent, RappiderChatModule,
  RappiderDropdownMenuModule, RappiderLottiePlayerModule
} from '@rappider/rappider-components';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { Route, RouterModule } from '@angular/router';
import { AuthGuard } from '@rappider/authentication/guards';
import { VerifyPersonGuard, ActiveProjectGuard } from '@rappider/shared/guards';
import { AIAssistantComponent } from './ai-assistant/ai-assistant.component';
import { PATH_DEFINITIONS } from '@rappider/shared/definitions';
import { NzMessageServiceModule } from 'ng-zorro-antd/message';
import { ChatInputComponent } from './components/chat-input/chat-input.component';
import { RappiderChatMessagesComponent } from './components/chat-messages/chat-messages.component';
import { CreateAiFunctionComponent } from './components/create-ai-function/create-ai-function.component';
import { RapiderAICrudComponent } from './modules/ai-function-call/ai-function-call-crud/ai-function-call-crud.component';
import { AiFunctionCallDataStoreModule } from '@rappider/shared/data-stores';
import { RappiderAiFunctionExecuteComponent } from './modules/ai-function-call/ai-function-execute/ai-function-execute.component';
import { AngularSplitModule } from 'angular-split';
import { RappiderAiHomeComponent } from './components/ai-home/ai-home.component';
import { RappiderAiActiveItemSelectorComponent } from './components/ai-active-item-selector/ai-active-item-selector.component';
import { AiCrudViewComponent } from './components/ai-crud-view/ai-crud-view.component';

import { RappiderDatasourcesComponent } from './components/datasources/datasources.component';
import { ProjectModelEntitiesComponent } from './components/project-model-entities/project-model-entities.component';
import { RappiderDatasourceNewOptionsComponent } from './components/datasource-new-options/datasource-new-options.component';
import { RappiderDatasourceAddWithFileComponent } from './components/datasource-add-with-file/datasource-add-with-file.component';
import { NzCardModule } from 'ng-zorro-antd/card';
import { OnboardingWidgetComponent } from './components/onboarding-widget/onboarding-widget.component';
import { RappiderFileExplorerConnectComponent } from 'libs/file-explorer/src/lib/components/connect/connect.component';
import { RappiderFileExplorerFileListComponent } from 'libs/file-explorer/src/lib/components/file-list/file-list.component';
import { RappiderSalesforceConnectComponent } from 'libs/salesforce/src/lib/components/salesforce-connect/saleforce-connect.component';
import { RappiderSalesforceModelListComponent } from
  'libs/salesforce/src/lib/components/salesforce-model-list/salesforce-model-list.component';
import { JoyrideModule } from 'ngx-joyride';
import { AiFunctionCallsComponent } from './components/ai-function-calls/ai-function-calls.component';

import { ContentTreeRendererModule } from '@rappider/content-tree-renderer';
import { UiPageRendererComponent } from 'libs/content-tree-renderer/src/lib/components/ui-page-renderer/ui-page-renderer.component';
import { AiAbstractChatComponent } from './components/ai-abstract-chat/ai-abstract-chat.component';
import { AiCanvasRendererComponent } from './components/ai-canvas-components/ai-canvas-renderer.component';
import { AiGenerateDataModelComponent } from './components/ai-generate-components/data-model/ai-generate-data-model.component';
import { ManageUiHomeComponent } from './components/manage-ui-home/manage-ui-home.component';
import { AiEntityVisualizationComponent } from './components/ai-entity-visualization/ai-entity-visualization.component';
import { ManageWorkflowsComponent } from './components/manage-workflows/manage-workflows.component';
import { ManageMessagingComponent } from './components/manage-messaging/manage-messaging.component';
import { ManageUiEntityComponent } from './components/manage-ui-entity/manage-ui-entity.component';
import { AiGenerateEntityComponent } from './components/ai-generate-components/entity-component/ai-generate-entity-component.component';


const routes: Route[] = [
  {
    path: '',
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: PATH_DEFINITIONS.AI.ASSISTANT,
      },
      {
        path: 'assistant',
        pathMatch: 'full',
        redirectTo: PATH_DEFINITIONS.AI.ASSISTANT,
      },
      {
        path: 'assistant/:channelId',
        component: AIAssistantComponent,
        canActivate: [AuthGuard, ActiveProjectGuard, VerifyPersonGuard],
        data: { title: 'AI Assistant', noInnerContentPadding: true, showPageTitle: true, layoutMode: 'projectDesign' },
        children: [
          {
            path: 'page/:pageId',
            component: UiPageRendererComponent
          },
          {
            path: '**',
            component: ProjectModelEntitiesComponent
          }
        ]
      },
      {
        path: 'generate-data-model',
        component: AiGenerateDataModelComponent,
        canActivate: [AuthGuard, ActiveProjectGuard, VerifyPersonGuard],
        data: { layoutMode: 'projectDesign' }
      },
      {
        path: 'generate-data-model/edit/:modelName',
        component: AiGenerateDataModelComponent,
        canActivate: [AuthGuard, ActiveProjectGuard, VerifyPersonGuard],
        data: { layoutMode: 'projectDesign' }
      },
      {
        path: 'generate-entity-component/:projectModelId',
        component: AiGenerateEntityComponent,
        canActivate: [AuthGuard, ActiveProjectGuard, VerifyPersonGuard],
        data: { layoutMode: 'projectDesign' }
      },
      {
        path: 'update-component/:componentDefinitionId',
        component: AiGenerateEntityComponent,
        canActivate: [AuthGuard, ActiveProjectGuard, VerifyPersonGuard],
        data: { layoutMode: 'projectDesign' }
      },
      {
        path: 'entity-visualization/:projectModelId',
        component: AiEntityVisualizationComponent,
        canActivate: [AuthGuard, ActiveProjectGuard, VerifyPersonGuard],
        data: { layoutMode: 'projectDesign' }
      },
      {
        path: 'manage-ui',
        component: ManageUiHomeComponent,
        canActivate: [AuthGuard, ActiveProjectGuard, VerifyPersonGuard],
        data: { layoutMode: 'projectDesign' }
      },
      {
        path: 'manage-ui/entity/:projectModelId',
        component: ManageUiEntityComponent,
        canActivate: [AuthGuard, ActiveProjectGuard, VerifyPersonGuard],
        data: { layoutMode: 'projectDesign' }
      },
      {
        path: 'manage-workflows',
        component: ManageWorkflowsComponent,
        canActivate: [AuthGuard, ActiveProjectGuard, VerifyPersonGuard],
        data: { layoutMode: 'projectDesign' }
      },
      {
        path: 'manage-messaging',
        component: ManageMessagingComponent,
        canActivate: [AuthGuard, ActiveProjectGuard, VerifyPersonGuard],
        data: { layoutMode: 'projectDesign' }
      },
      {
        path: 'project-database',
        component: ProjectModelEntitiesComponent,
        canActivate: [AuthGuard, ActiveProjectGuard, VerifyPersonGuard],
        data: { layoutMode: 'projectDesign' }
      },
      {
        path: 'create-ai-function',
        component: CreateAiFunctionComponent,
        canActivate: [AuthGuard, ActiveProjectGuard, VerifyPersonGuard],
        data: { layoutMode: 'projectDesign' }
      },
      {
        path: 'ai-function',
        component: AiFunctionCallsComponent,
        canActivate: [AuthGuard, ActiveProjectGuard, VerifyPersonGuard],
        data: { layoutMode: 'projectDesign' }
      },
      {
        path: 'ai-function/:viewMode',
        component: AiFunctionCallsComponent,
        canActivate: [AuthGuard, ActiveProjectGuard, VerifyPersonGuard],
        data: { layoutMode: 'projectDesign' }
      },
      {
        path: 'ai-function/:viewMode/:itemId',
        component: AiFunctionCallsComponent,
        canActivate: [AuthGuard, ActiveProjectGuard, VerifyPersonGuard],
        data: { layoutMode: 'projectDesign' }
      },
      {
        path: 'ai-function-execute/:itemId',
        component: RappiderAiFunctionExecuteComponent,
        canActivate: [AuthGuard, ActiveProjectGuard, VerifyPersonGuard],
        data: { layoutMode: 'projectDesign' }
      },
      {
        path: 'datasources',
        component: RappiderDatasourcesComponent,
        canActivate: [AuthGuard, ActiveProjectGuard, VerifyPersonGuard],
        data: { title: 'Data Sources', showPageTitle: true, layoutMode: 'projectDesign' }
      },
      {
        path: 'datasources/create',
        component: RappiderDatasourceNewOptionsComponent,
        canActivate: [AuthGuard, ActiveProjectGuard, VerifyPersonGuard],
        data: { title: 'Connect Data Source', showPageTitle: true, layoutMode: 'projectDesign' }
      },
      {
        path: 'datasources/create/:dataSourceKey',
        component: RappiderDatasourceAddWithFileComponent,
        canActivate: [AuthGuard, ActiveProjectGuard, VerifyPersonGuard],
        data: { title: 'Connect Data Source', showPageTitle: true, layoutMode: 'projectDesign' }
      },
      {
        path: 'file-explorer-connect',
        component: RappiderFileExplorerConnectComponent,
        canActivate: [AuthGuard, ActiveProjectGuard, VerifyPersonGuard],
        data: { title: 'Connect Drive', showPageTitle: true, layoutMode: 'projectDesign' }
      },
      {
        path: 'datasources/file-explorer-file-list',
        component: RappiderFileExplorerFileListComponent,
        canActivate: [AuthGuard, ActiveProjectGuard, VerifyPersonGuard],
        data: { title: 'Connect File Explorer File List', showPageTitle: true, layoutMode: 'projectDesign' }
      },
      {
        path: 'datasources/salesforce-connect',
        component: RappiderSalesforceConnectComponent,
        canActivate: [AuthGuard, ActiveProjectGuard, VerifyPersonGuard],
        data: { title: 'Salesforce Connect', showPageTitle: true, layoutMode: 'projectDesign' }
      },
      {
        path: 'datasources/salesforce-model-list',
        component: RappiderSalesforceModelListComponent,
        canActivate: [AuthGuard, ActiveProjectGuard, VerifyPersonGuard],
        data: { title: 'Salesforce Model List', showPageTitle: true, layoutMode: 'projectDesign' }
      }
    ]
  }
];

@NgModule({
  imports: [
    CommonModule,
    RouterModule.forChild(routes),
    AiFunctionCallDataStoreModule,
    NzInputModule,
    NzIconModule,
    NzButtonModule,
    FormsModule,
    NzRadioModule,
    RappiderLottiePlayerModule,
    NzToolTipModule,
    RappiderChatModule,
    NzMessageServiceModule,
    AngularSplitModule,
    NzCardModule,
    JoyrideModule.forRoot(),
    ContentTreeRendererModule,
    RappiderCardWithIconTitleDescriptionListComponent,
    RappiderDropdownMenuModule,
    ChatInputComponent,
    RappiderChatMessagesComponent,
    CreateAiFunctionComponent,
    RapiderAICrudComponent,
    RappiderAiHomeComponent,
    RappiderAiActiveItemSelectorComponent,
    AiCrudViewComponent,
    RappiderDatasourcesComponent,
    ProjectModelEntitiesComponent,
    OnboardingWidgetComponent,
    AiAbstractChatComponent,
    AiCanvasRendererComponent,
    AiGenerateDataModelComponent,
    AiGenerateEntityComponent,
    ManageUiHomeComponent,
    AiEntityVisualizationComponent,
    ManageWorkflowsComponent,
    ManageMessagingComponent,
    RappiderButtonModule
  ],
  declarations: [
    AIAssistantComponent
  ],
  exports: [
    AIAssistantComponent
  ]
})
export class AiIntegrationModule { }


